import { DailyMetrics, WeeklyCheckinEntry } from '../types';
import { getPillarLabel, KEY_LAB_META } from './dashboardHelpers';
import { HomeLeavingDay } from '../hooks/useDashboardData';

/* ── Concern types ──────────────────────────────────────────────────── */
export type ConcernType =
  | 'phq4_severe' | 'phq4_moderate' | 'phq4_mild'
  | 'substance_problematic' | 'substance_elevated'
  | 'stress_high' | 'stress_elevated'
  | 'bedtime_variable' | 'bedtime_drifting' | 'bedtime_late'
  | 'lab_deficiency' | 'lab_summary'
  | 'sleep_poor' | 'sleep_attention'
  | 'hrv_low' | 'hrv_below'
  | 'readiness_low' | 'readiness_below'
  | 'diet_poor' | 'diet_fair'
  | 'social_isolation' | 'social_limited'
  | 'movement_low'
  | 'time_outside_low';

/* ── Core interfaces ────────────────────────────────────────────────── */
export interface FocusArea {
  priority: number;
  title: string;
  detail: string;
  color: string;
  link: string;
  concernType: ConcernType;
  meta?: Record<string, string | number>;
}

export type FocusCategory = 'Sleep' | 'Recovery' | 'Nutrients' | 'Activity' | 'Stress' | 'Mental Health' | 'Social' | 'Lifestyle' | 'Nutrition' | 'Health';

export interface FocusAreaPresentation {
  category: FocusCategory;
  patientTitle: string;
  patientSubtitle: string;
  whySurfaced: string;
  nextSteps: string[];
  clinicalContext?: string;
}

export interface RichFocusArea extends FocusArea {
  presentation: FocusAreaPresentation;
}

/* ── Presentation config ────────────────────────────────────────────── */

const LAB_PATIENT_TITLES: Record<string, { title: string; subtitle: string }> = {
  'Vitamin D':       { title: 'Consider vitamin D with a meal',             subtitle: 'Your vitamin D level may benefit from consistent supplementation' },
  '25-OH Vitamin D': { title: 'Consider vitamin D with a meal',             subtitle: 'Your vitamin D level may benefit from consistent supplementation' },
  'Magnesium':       { title: 'Consider magnesium before bed',              subtitle: 'Magnesium can support sleep quality and stress resilience' },
  'RBC Magnesium':   { title: 'Consider magnesium before bed',              subtitle: 'Magnesium can support sleep quality and stress resilience' },
  'Vitamin B12':     { title: 'Support your B12 levels',                    subtitle: 'B12 plays a role in energy, mood, and nervous system health' },
  'Ferritin':        { title: 'Focus on iron-rich foods this week',         subtitle: 'Iron stores can affect energy and mood — simple dietary shifts may help' },
  'Omega-3 Index':   { title: 'Support recovery with omega-3',             subtitle: 'Omega-3s can support mood, inflammation balance, and brain health' },
  'Folate':          { title: 'Consider folate support',                    subtitle: 'Folate supports methylation pathways important for mood regulation' },
  'RBC Folate':      { title: 'Consider folate support',                    subtitle: 'Folate supports methylation pathways important for mood regulation' },
  'Homocysteine':    { title: 'Support methylation with B vitamins',        subtitle: 'B-complex (B6, B12, folate) may help optimize homocysteine levels' },
  'hsCRP':           { title: 'Support recovery with omega-3 and sleep',    subtitle: 'Anti-inflammatory habits like sleep, movement, and omega-3 may help' },
  'Insulin':         { title: 'Focus on blood sugar balance',               subtitle: 'Reducing refined carbs and walking after meals can support metabolic health' },
  'Fasting Insulin': { title: 'Focus on blood sugar balance',               subtitle: 'Reducing refined carbs and walking after meals can support metabolic health' },
  'Cortisol':        { title: 'Prioritize sleep and stress management',     subtitle: 'Cortisol patterns may reflect chronic stress — consistent routines can help' },
  'TSH':             { title: 'Discuss thyroid function with your clinician', subtitle: 'Thyroid markers can affect energy, mood, and metabolism' },
};

/* ── Actionable supplement hints per marker ─────────────────────────── */
const LAB_ACTIONS: Record<string, string> = {
  'Vitamin D':       'consider 2,000–4,000 IU D3 daily with fat',
  '25-OH Vitamin D': 'consider 2,000–4,000 IU D3 daily with fat',
  'Magnesium':       'consider 300–400mg glycinate at bedtime',
  'RBC Magnesium':   'consider 300–400mg glycinate at bedtime',
  'Vitamin B12':     'consider methylcobalamin 1,000mcg sublingual',
  'Ferritin':        'iron-rich foods or gentle iron supplement with vitamin C',
  'Omega-3 Index':   'consider 2g EPA+DHA fish oil daily',
  'Folate':          'consider methylfolate 400–800mcg daily',
  'RBC Folate':      'consider methylfolate 400–800mcg daily',
  'Homocysteine':    'B-complex (B6 + B12 + folate) can help lower levels',
  'hsCRP':           'anti-inflammatory focus: omega-3, sleep, stress reduction',
  'Insulin':         'reduce refined carbs, increase movement after meals',
  'Fasting Insulin': 'reduce refined carbs, increase movement after meals',
  'Cortisol':        'prioritize sleep consistency and stress management',
  'TSH':             'discuss thyroid function with your provider',
};

const FOCUS_PRESENTATION: Record<ConcernType, (area: FocusArea) => FocusAreaPresentation> = {
  // ── Mental Health ──
  phq4_severe: () => ({
    category: 'Mental Health',
    patientTitle: 'Connect with your care team soon',
    patientSubtitle: 'Your recent screening suggests reaching out to a clinician may be a helpful next step',
    whySurfaced: 'Your PHQ-4 screening score is significantly elevated, which can indicate significant distress.',
    nextSteps: ['Reach out to your clinician or therapist', 'Call a crisis line if you need immediate support', 'Complete a follow-up check-in when ready'],
    clinicalContext: 'PHQ-4 significantly elevated (≥9). Recommend clinical follow-up and safety assessment. Consider full PHQ-9 and GAD-7.',
  }),
  phq4_moderate: () => ({
    category: 'Mental Health',
    patientTitle: 'Consider scheduling a check-in',
    patientSubtitle: 'Your mood screening suggests a conversation with your care team may be worthwhile',
    whySurfaced: 'Your PHQ-4 screening score is moderately elevated, suggesting some emotional difficulty.',
    nextSteps: ['Schedule a check-in with your clinician', 'Continue weekly mood tracking', 'Prioritize sleep and social connection'],
    clinicalContext: 'PHQ-4 moderately elevated (6–8). Monitor weekly. Consider full PHQ-9 and GAD-7 if persistent.',
  }),
  phq4_mild: () => ({
    category: 'Mental Health',
    patientTitle: 'Keep tracking how you\'re feeling',
    patientSubtitle: 'Mild symptoms are common — consistent check-ins help identify patterns early',
    whySurfaced: 'Your PHQ-4 screening score is mildly elevated.',
    nextSteps: ['Continue weekly check-ins', 'Notice what helps your mood day to day', 'Reach out if things shift'],
  }),

  // ── Substances ──
  substance_problematic: () => ({
    category: 'Lifestyle',
    patientTitle: 'Explore strategies to reduce substance use',
    patientSubtitle: 'Your check-in flagged substance use as a concern — small changes can make a real difference',
    whySurfaced: 'Your most recent check-in reported problematic substance use.',
    nextSteps: ['Talk to your clinician about harm reduction', 'Track patterns around triggers', 'Consider support groups or counseling'],
    clinicalContext: 'Self-reported problematic substance use. Recommend motivational interviewing and harm reduction discussion.',
  }),
  substance_elevated: () => ({
    category: 'Lifestyle',
    patientTitle: 'Reflect on your relationship with substances',
    patientSubtitle: 'Elevated use can be worth examining — no judgment, just awareness',
    whySurfaced: 'Your most recent check-in reported elevated substance use.',
    nextSteps: ['Notice when and why you reach for substances', 'Consider one small substitution this week', 'Discuss with your clinician if helpful'],
  }),

  // ── Stress ──
  stress_high: () => ({
    category: 'Stress',
    patientTitle: 'Make space for stress relief today',
    patientSubtitle: 'Your check-in reflects high stress — even 5 minutes of breathing or a short walk can help',
    whySurfaced: 'Your most recent check-in reported feeling overwhelmed by stress.',
    nextSteps: ['Try 5 minutes of slow, deep breathing', 'Step outside for fresh air if possible', 'Write down one thing you can let go of today'],
    clinicalContext: 'Self-reported overwhelmed. Screen for burnout, anxiety, and assess coping strategies.',
  }),
  stress_elevated: () => ({
    category: 'Stress',
    patientTitle: 'Add a calming activity to your day',
    patientSubtitle: 'You reported some stress — building in small recovery moments can help',
    whySurfaced: 'Your most recent check-in reported struggling with stress.',
    nextSteps: ['Try a short walk or stretch break', 'Limit news and social media before bed', 'Consider a brief mindfulness practice'],
  }),

  // ── Bedtime ──
  bedtime_variable: (area) => ({
    category: 'Sleep',
    patientTitle: 'Work toward a consistent bedtime',
    patientSubtitle: 'Your bedtime has varied widely — a steady schedule can support deeper sleep and recovery',
    whySurfaced: `Your bedtime over the past week varied by ${area.meta?.driftMins ?? ''}+ minutes. Irregular sleep timing can disrupt circadian rhythm and reduce sleep quality.`,
    nextSteps: ['Set a target bedtime and stick within ±30 minutes', 'Start your wind-down routine at the same time each night', 'Avoid caffeine after 2pm to make earlier bedtimes easier'],
    clinicalContext: `Bedtime SD ±${area.meta?.driftMins ?? '?'}min. High circadian variability associated with worse mood outcomes (JAMA Psychiatry 2026).`,
  }),
  bedtime_drifting: (area) => ({
    category: 'Sleep',
    patientTitle: 'Aim for a more consistent bedtime',
    patientSubtitle: 'Small shifts in sleep timing can add up — consistency supports your body\'s natural rhythm',
    whySurfaced: `Your bedtime varied by about ${area.meta?.driftMins ?? ''} minutes recently. Even moderate variation can affect sleep quality.`,
    nextSteps: ['Pick a bedtime window and aim for it most nights', 'Use a gentle alarm 30 minutes before your target', 'Keep weekends within 30 minutes of weekday timing'],
  }),
  bedtime_late: (area) => ({
    category: 'Sleep',
    patientTitle: 'Try moving your bedtime earlier',
    patientSubtitle: 'Several late nights were detected — earlier bedtimes can improve deep sleep and next-day energy',
    whySurfaced: `${area.meta?.lateBedtimes ?? 'Several'} nights this week were past 12:30am. Late bedtimes are associated with reduced deep sleep.`,
    nextSteps: ['Start winding down 30 minutes earlier than usual', 'Dim lights and reduce screen brightness after 10pm', 'Avoid stimulating activities close to midnight'],
  }),

  // ── Labs ──
  lab_deficiency: (area) => {
    const marker = String(area.meta?.marker ?? '');
    const value = area.meta?.value ?? '';
    const unit = String(area.meta?.unit ?? '');
    const optRange = area.meta?.optLow != null && area.meta?.optHigh != null
      ? `${area.meta.optLow}–${area.meta.optHigh} ${unit}` : '';
    const labTitle = LAB_PATIENT_TITLES[marker];
    return {
      category: 'Nutrients',
      patientTitle: labTitle?.title ?? `Review ${marker} with your clinician`,
      patientSubtitle: labTitle?.subtitle ?? 'A lab marker is outside its optimal range — your clinician can advise on next steps',
      whySurfaced: `Your ${marker} level (${value} ${unit}) is outside the optimal range${optRange ? ` of ${optRange}` : ''}. This marker is tracked because of its evidence-based connection to mood and energy.`,
      nextSteps: [
        LAB_ACTIONS[marker] ? `${LAB_ACTIONS[marker].charAt(0).toUpperCase()}${LAB_ACTIONS[marker].slice(1)}` : `Discuss ${marker} with your provider`,
        'Retest in 8–12 weeks to track progress',
        'Discuss any new supplements with your clinician first',
      ],
      clinicalContext: `${marker} ${value} ${unit}${optRange ? ` (functional optimal: ${optRange})` : ''}. Biomarkers should be interpreted in clinical context — single values may not reflect the full picture.`,
    };
  },
  lab_summary: (area) => ({
    category: 'Nutrients',
    patientTitle: 'Review your lab results in detail',
    patientSubtitle: 'Several markers are outside their optimal ranges — your Labs tab has the full picture',
    whySurfaced: area.detail,
    nextSteps: ['Review your Labs tab for specific markers', 'Discuss findings with your clinician', 'Consider retesting in 8–12 weeks'],
    clinicalContext: 'Multiple biomarkers outside functional optimal range. Recommend comprehensive review.',
  }),

  // ── Sleep ──
  sleep_poor: (area) => ({
    category: 'Sleep',
    patientTitle: 'Prioritize your wind-down routine tonight',
    patientSubtitle: 'Last night\'s sleep score was low — a consistent routine can help improve tomorrow\'s score',
    whySurfaced: `Your sleep score was ${area.meta?.score ?? '?'}/100, which is well below the healthy range of 75+.`,
    nextSteps: ['Start winding down 30–60 minutes before bed', 'Keep your bedroom cool and dark', 'Avoid screens in the last hour before sleep'],
    clinicalContext: `Sleep score ${area.meta?.score ?? '?'}/100. Poor sleep quality is a strong predictor of mood disturbance and cognitive impairment.`,
  }),
  sleep_attention: (area) => ({
    category: 'Sleep',
    patientTitle: 'Small sleep improvements can add up',
    patientSubtitle: 'Your sleep score was slightly below target — minor adjustments may help',
    whySurfaced: `Your sleep score was ${area.meta?.score ?? '?'}/100, below the target of 75+.`,
    nextSteps: ['Limit screens 1 hour before bed', 'Try a consistent bedtime this week', 'Avoid caffeine after early afternoon'],
  }),

  // ── HRV ──
  hrv_low: (area) => ({
    category: 'Recovery',
    patientTitle: 'Prioritize rest and recovery today',
    patientSubtitle: 'Your body may benefit from lighter activity, hydration, and an earlier bedtime',
    whySurfaced: `Your heart rate variability (HRV) was ${area.meta?.hrv ?? '?'}ms, which is below the typical recovery threshold. This can reflect physical or emotional stress.`,
    nextSteps: ['Try 5 minutes of slow breathing or meditation', 'Consider lighter exercise today', 'Focus on hydration and getting to bed on time'],
    clinicalContext: `HRV ${area.meta?.hrv ?? '?'}ms (<30ms threshold). Low HRV may indicate parasympathetic withdrawal — consider acute stressors, illness, or overtraining.`,
  }),
  hrv_below: (area) => ({
    category: 'Recovery',
    patientTitle: 'Support your recovery with gentle movement',
    patientSubtitle: 'Your HRV is below your typical baseline — breathing exercises and light activity may help',
    whySurfaced: `Your HRV was ${area.meta?.hrv ?? '?'}ms, below the 50ms baseline. This suggests your nervous system is under more load than usual.`,
    nextSteps: ['Try a light walk or gentle yoga', 'Practice box breathing (4-4-4-4)', 'Aim for 7+ hours of sleep tonight'],
  }),

  // ── Readiness ──
  readiness_low: (area) => ({
    category: 'Recovery',
    patientTitle: 'Consider a lighter day for recovery',
    patientSubtitle: 'Your readiness score suggests your body could use more rest today',
    whySurfaced: `Your readiness score was ${area.meta?.score ?? '?'}/100. Scores below 60 suggest your body hasn't fully recovered.`,
    nextSteps: ['Avoid intense exercise today', 'Prioritize hydration and nutrition', 'Get to bed earlier tonight if possible'],
    clinicalContext: `Readiness ${area.meta?.score ?? '?'}/100. Low readiness reflects composite recovery deficit across sleep, HRV, and activity load.`,
  }),
  readiness_below: (area) => ({
    category: 'Recovery',
    patientTitle: 'Listen to your body\'s recovery signals',
    patientSubtitle: 'Your readiness score is slightly below target — balance activity with recovery',
    whySurfaced: `Your readiness score was ${area.meta?.score ?? '?'}/100, below the target of 75+.`,
    nextSteps: ['Balance today\'s activity with adequate rest', 'Pay attention to how you feel during exercise', 'Support recovery with good sleep tonight'],
  }),

  // ── Diet ──
  diet_poor: () => ({
    category: 'Nutrition',
    patientTitle: 'Focus on whole foods and protein today',
    patientSubtitle: 'Your check-in flagged diet as a concern — even one balanced meal can help',
    whySurfaced: 'Your most recent check-in reported poor diet quality. Nutrition directly impacts energy, mood, and recovery.',
    nextSteps: ['Aim for one meal with protein, vegetables, and healthy fat', 'Stay hydrated throughout the day', 'Prepare a simple healthy option for tomorrow'],
    clinicalContext: 'Self-reported poor diet quality. Assess for appetite changes, food access, or disordered eating patterns.',
  }),
  diet_fair: () => ({
    category: 'Nutrition',
    patientTitle: 'Build on your nutrition habits this week',
    patientSubtitle: 'Your diet is fair — small improvements in food quality can support mood and energy',
    whySurfaced: 'Your most recent check-in reported fair diet quality.',
    nextSteps: ['Add one extra serving of vegetables today', 'Choose whole grains over refined options', 'Consider meal prepping for consistency'],
  }),

  // ── Social ──
  social_isolation: () => ({
    category: 'Social',
    patientTitle: 'Reach out to someone you care about',
    patientSubtitle: 'Social connection is a powerful protective factor — even a brief text or call can help',
    whySurfaced: 'Your most recent check-in indicated very limited social engagement. Social isolation is a significant risk factor for mood difficulties.',
    nextSteps: ['Send a message to a friend or family member', 'Accept one social invitation this week', 'Consider joining a group activity or class'],
    clinicalContext: 'Self-reported minimal social engagement. Screen for social anxiety, depression-related withdrawal, or situational factors.',
  }),
  social_limited: () => ({
    category: 'Social',
    patientTitle: 'Make time for a meaningful connection',
    patientSubtitle: 'Your check-in suggests limited social engagement — connection supports wellbeing',
    whySurfaced: 'Your most recent check-in indicated limited social engagement.',
    nextSteps: ['Schedule a coffee or walk with someone this week', 'Call or message someone you haven\'t talked to recently', 'Consider what makes connection easier for you'],
  }),

  // ── Movement ──
  movement_low: (area) => ({
    category: 'Activity',
    patientTitle: 'Add a short walk to your day',
    patientSubtitle: 'Yesterday\'s step count was low — even a brief walk can boost mood and energy',
    whySurfaced: `You logged ${area.meta?.steps != null ? Number(area.meta.steps).toLocaleString() : '?'} steps yesterday. Regular movement supports mood, sleep quality, and metabolic health.`,
    nextSteps: ['Try a 15–20 minute walk after lunch', 'Take phone calls while walking', 'Set a gentle reminder to stand and move every hour'],
    clinicalContext: `${area.meta?.steps != null ? Number(area.meta.steps).toLocaleString() : '?'} steps (target ≥7,000). Low daily movement correlates with worse mood and sleep outcomes.`,
  }),

  // ── Time Outside ──
  time_outside_low: (area) => ({
    category: 'Activity',
    patientTitle: 'Fresh air and sunlight can help',
    patientSubtitle: 'Getting outside — even briefly — supports mood, energy, and sleep quality',
    whySurfaced: 'You\'ve spent more time at home recently. Sunlight and fresh air have a meaningful impact on circadian rhythm and wellbeing.',
    nextSteps: [
      'Step outside for even 10 minutes today',
      'A short walk, coffee on the porch, or sitting in a park all count',
      'Morning sunlight is especially helpful for sleep and energy',
    ],
    clinicalContext: `Home-bound ${area.meta?.homeDays ?? '?'} of last 7 days. Behavioral withdrawal is a key indicator for depression relapse. Assess for anhedonia and social avoidance.`,
  }),
};

/* ── Fallback for unknown types ─────────────────────────────────────── */
function fallbackPresentation(area: FocusArea): FocusAreaPresentation {
  return {
    category: 'Health',
    patientTitle: 'Review this area with your care team',
    patientSubtitle: 'A health metric was flagged — your clinician can help interpret the details',
    whySurfaced: area.detail,
    nextSteps: ['Review the details below', 'Discuss with your clinician at your next visit'],
  };
}

/* ── Transformer ────────────────────────────────────────────────────── */
export function toRichFocusArea(area: FocusArea): RichFocusArea {
  const presenter = FOCUS_PRESENTATION[area.concernType];
  return {
    ...area,
    presentation: presenter ? presenter(area) : fallbackPresentation(area),
  };
}

/* ── Bedtime consistency from recent metrics ────────────────────────── */
function bedtimeConsistency(metrics: DailyMetrics[]): { driftMins: number; lateBedtimes: number } | null {
  const bedtimes = metrics
    .filter(d => d.bedtime_start != null)
    .map(d => {
      const dt = new Date(d.bedtime_start!);
      let mins = dt.getHours() * 60 + dt.getMinutes();
      if (mins < 360) mins += 1440;
      return mins;
    });
  if (bedtimes.length < 3) return null;

  const avg = bedtimes.reduce((a, b) => a + b, 0) / bedtimes.length;
  const driftMins = Math.round(Math.sqrt(bedtimes.reduce((sum, b) => sum + (b - avg) ** 2, 0) / bedtimes.length));
  const lateBedtimes = bedtimes.filter(b => b > 24 * 60 + 30).length;
  return { driftMins, lateBedtimes };
}

/* ── Input type ─────────────────────────────────────────────────────── */
interface FocusAreaInput {
  yesterday: DailyMetrics | null;
  latestHrvMetric: DailyMetrics | null;
  latestCheckin: WeeklyCheckinEntry | null;
  phq4ScreeningLevel: 'normal' | 'mild' | 'moderate' | 'severe';
  keyLabs: { marker: string; value: number; unit: string; ref_low: number | null; ref_high: number | null; panel: string; date: string }[];
  recentMetrics?: DailyMetrics[];
  homeLeaving?: HomeLeavingDay[];
}

/* ── Core ranking engine (unchanged logic) ──────────────────────────── */
export function computeFocusAreas(input: FocusAreaInput): FocusArea[] {
  const { yesterday, latestHrvMetric, latestCheckin, phq4ScreeningLevel, keyLabs, recentMetrics } = input;
  const concerns: FocusArea[] = [];

  // PHQ-4 mental health screening
  if (phq4ScreeningLevel === 'severe') {
    concerns.push({ priority: 10, title: 'Mental health — significantly elevated', detail: 'PHQ-4 significantly elevated · clinician review recommended', color: '#EF4444', link: '/phenotype', concernType: 'phq4_severe' });
  } else if (phq4ScreeningLevel === 'moderate') {
    concerns.push({ priority: 8, title: 'Mental health — moderately elevated', detail: 'PHQ-4 moderately elevated · consider scheduling a check-in', color: '#F59E0B', link: '/phenotype', concernType: 'phq4_moderate' });
  } else if (phq4ScreeningLevel === 'mild') {
    concerns.push({ priority: 4, title: 'Mental health — mildly elevated', detail: 'PHQ-4 mildly elevated · worth monitoring weekly', color: '#F59E0B', link: '/phenotype', concernType: 'phq4_mild' });
  }

  // Substance use (0 = Problematic, 1 = Elevated)
  if (latestCheckin?.substances != null && latestCheckin.substances <= 1) {
    concerns.push({
      priority: latestCheckin.substances === 0 ? 9 : 6,
      title: 'Substance use flagged',
      detail: `Check-in: ${getPillarLabel('substances', latestCheckin.substances)} · harm reduction discussion`,
      color: latestCheckin.substances === 0 ? '#EF4444' : '#F59E0B',
      link: '/checkin',
      concernType: latestCheckin.substances === 0 ? 'substance_problematic' : 'substance_elevated',
    });
  }

  // Stress self-report (0 = Overwhelmed, 1 = Struggling)
  if (latestCheckin?.stress != null && latestCheckin.stress <= 1) {
    concerns.push({
      priority: latestCheckin.stress === 0 ? 8 : 5,
      title: latestCheckin.stress === 0 ? 'High stress self-report' : 'Elevated stress self-report',
      detail: `Check-in: ${getPillarLabel('stress', latestCheckin.stress)} · stress management focus`,
      color: latestCheckin.stress === 0 ? '#EF4444' : '#F59E0B',
      link: '/checkin',
      concernType: latestCheckin.stress === 0 ? 'stress_high' : 'stress_elevated',
    });
  }

  // Bedtime consistency
  if (recentMetrics && recentMetrics.length >= 3) {
    const bt = bedtimeConsistency(recentMetrics);
    if (bt) {
      if (bt.driftMins > 60) {
        concerns.push({ priority: 7, title: 'Bedtime varies widely', detail: `±${bt.driftMins}min variation · aim for same bedtime ±30min`, color: '#EF4444', link: '/sleep', concernType: 'bedtime_variable', meta: { driftMins: bt.driftMins } });
      } else if (bt.driftMins > 30) {
        concerns.push({ priority: 5, title: 'Bedtime consistency drifting', detail: `±${bt.driftMins}min variation · consistent bedtime supports circadian rhythm`, color: '#F59E0B', link: '/sleep', concernType: 'bedtime_drifting', meta: { driftMins: bt.driftMins } });
      }
      if (bt.lateBedtimes >= 3) {
        concerns.push({ priority: 6, title: 'Late bedtimes detected', detail: `${bt.lateBedtimes} nights past 12:30am this week · earlier bedtime improves deep sleep`, color: '#F59E0B', link: '/sleep', concernType: 'bedtime_late', meta: { lateBedtimes: bt.lateBedtimes } });
      }
    }
  }

  // Individual lab deficiencies
  const labsOut = keyLabs.filter(l => {
    const m = KEY_LAB_META[l.marker];
    if (!m) return false;
    if (m.optLow != null && l.value < m.optLow) return true;
    if (m.optHigh != null && m.optHigh < 9999 && l.value > m.optHigh) return true;
    return false;
  });
  labsOut.slice(0, 2).forEach(l => {
    const m = KEY_LAB_META[l.marker];
    const action = LAB_ACTIONS[l.marker];
    const rangeStr = m?.optLow != null && m?.optHigh != null && m.optHigh < 9999
      ? ` · optimal ${m.optLow}–${m.optHigh} ${l.unit}` : '';
    concerns.push({
      priority: 6,
      title: `${l.marker} ${l.value} ${l.unit} — outside optimal`,
      detail: action ? `${action}${rangeStr}` : `Review with provider${rangeStr}`,
      color: '#F59E0B',
      link: '/labs',
      concernType: 'lab_deficiency',
      meta: { marker: l.marker, value: l.value, unit: l.unit, optLow: m?.optLow ?? 0, optHigh: m?.optHigh ?? 0 },
    });
  });
  if (labsOut.length > 2) {
    concerns.push({
      priority: 4,
      title: `${labsOut.length - 2} more lab markers outside optimal`,
      detail: `${labsOut.slice(2).map(l => l.marker).join(', ')} · review in Labs`,
      color: '#F59E0B',
      link: '/labs',
      concernType: 'lab_summary',
    });
  }

  // Sleep
  const sleepScore = yesterday?.sleep_score;
  if (sleepScore != null) {
    if (sleepScore < 60) {
      concerns.push({ priority: 9, title: 'Poor sleep quality', detail: `Score ${sleepScore}/100 · prioritize consistent bedtime and wind-down routine`, color: '#EF4444', link: '/sleep', concernType: 'sleep_poor', meta: { score: sleepScore } });
    } else if (sleepScore < 75) {
      concerns.push({ priority: 5, title: 'Sleep needs attention', detail: `Score ${sleepScore}/100 · target ≥75 · limit screens 1hr before bed`, color: '#F59E0B', link: '/sleep', concernType: 'sleep_attention', meta: { score: sleepScore } });
    }
  }

  // HRV
  const hrvVal = latestHrvMetric?.hrv;
  if (hrvVal != null) {
    if (hrvVal < 30) {
      concerns.push({ priority: 7, title: 'HRV recovery low', detail: `${hrvVal}ms · prioritize rest, hydration, and stress reduction today`, color: '#EF4444', link: '/sleep', concernType: 'hrv_low', meta: { hrv: hrvVal } });
    } else if (hrvVal < 50) {
      concerns.push({ priority: 4, title: 'HRV below baseline', detail: `${hrvVal}ms · recovery focus: sleep, breathing exercises, light movement`, color: '#F59E0B', link: '/sleep', concernType: 'hrv_below', meta: { hrv: hrvVal } });
    }
  }

  // Readiness
  const readiness = yesterday?.readiness;
  if (readiness != null && readiness < 60) {
    concerns.push({ priority: 6, title: 'Low readiness score', detail: `Score ${readiness}/100 · body signalling need for recovery day`, color: '#EF4444', link: '/sleep', concernType: 'readiness_low', meta: { score: readiness } });
  } else if (readiness != null && readiness < 75) {
    concerns.push({ priority: 3, title: 'Readiness below target', detail: `Score ${readiness}/100 · target ≥75`, color: '#F59E0B', link: '/sleep', concernType: 'readiness_below', meta: { score: readiness } });
  }

  // Diet
  if (latestCheckin?.diet != null && latestCheckin.diet < 2) {
    concerns.push({
      priority: latestCheckin.diet === 0 ? 6 : 3,
      title: 'Diet quality below target',
      detail: `Check-in: ${getPillarLabel('diet', latestCheckin.diet)} · focus on whole foods, protein, and vegetables`,
      color: latestCheckin.diet === 0 ? '#EF4444' : '#F59E0B',
      link: '/checkin',
      concernType: latestCheckin.diet === 0 ? 'diet_poor' : 'diet_fair',
    });
  }

  // Social
  if (latestCheckin?.social != null && latestCheckin.social < 2) {
    concerns.push({
      priority: latestCheckin.social === 0 ? 7 : 3,
      title: 'Social isolation',
      detail: `Check-in: ${getPillarLabel('social', latestCheckin.social)} · reach out to one person today`,
      color: latestCheckin.social === 0 ? '#EF4444' : '#F59E0B',
      link: '/checkin',
      concernType: latestCheckin.social === 0 ? 'social_isolation' : 'social_limited',
    });
  }

  // Time outside (home-leaving)
  if (input.homeLeaving && input.homeLeaving.length >= 3) {
    const recent7 = [...input.homeLeaving]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 7);
    const homeDays = recent7.filter(d => !d.leftHome).length;

    // Also check consecutive streak
    let streak = 0;
    for (const day of recent7) {
      if (!day.leftHome) streak++;
      else break;
    }

    if (homeDays >= 2 || streak >= 2) {
      concerns.push({
        priority: streak >= 3 ? 7 : 5,
        title: 'Getting outside could help',
        detail: 'Fresh air and sunlight support mood, energy, and sleep',
        color: streak >= 3 ? '#EF4444' : '#F59E0B',
        link: '/activity',
        concernType: 'time_outside_low',
        meta: { homeDays, streak },
      });
    }
  }

  // Movement
  const steps = yesterday?.steps;
  if (steps != null && steps < 5000) {
    concerns.push({ priority: 3, title: 'Low daily movement', detail: `${steps.toLocaleString()} steps · try a 20-min walk after lunch`, color: '#F59E0B', link: '/activity', concernType: 'movement_low', meta: { steps } });
  }

  // Sort by priority descending, return top 3
  concerns.sort((a, b) => b.priority - a.priority);
  return concerns.slice(0, 3);
}
